import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import kebabCase from 'lodash/kebabCase';

import { useAnalytics } from '@ecosystem/newron-client-lib';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { SalesPerformance } from '@models/SalesPerformance';
import { NoResult } from '@pages/NoResult';
import { PERFORMANCE_CARD_TITLE } from '@pages/Utilities';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  pageElementViewTrackingFunction,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { getAppliedPgAndCountriesInfo } from '@utils/ProductGroupsAvailabilityUtils';
import { KpiTextForTracking, PerformanceType } from '@const/PerformanceType';
import { PosFactTile } from '@components/pos-fact-tile/PosFactTile';
import { getPosFactTileConfig, PosFactTileConfig } from '@components/pos-fact-tile/PosFactTileConfig';
import { WithTile, WithTiles } from '@components/design';
import { constructRevenueTitle } from '@utils/currencyUtils/CurrencyUtils';
import ErrorResultMessage from '@pages/ErrorResultMessage';

interface PosFactsTilesProps {
  revenuePerformance?: SalesPerformance;
  volumePerformance?: SalesPerformance;
  selectedGrowthType: GrowthTypes;
  isLoading: boolean;
  factRevenue: FactRevenue;
  performanceType?: PerformanceType;
  volume: string;
  showGrowthIcon?: boolean;
  subSectionForAmplitudeTracking: SubSection;
  isApiError?: boolean;
}

const MarketPerformancePosFactsTiles: React.FC<PosFactsTilesProps> = ({
  revenuePerformance,
  volumePerformance,
  selectedGrowthType,
  isLoading,
  factRevenue,
  performanceType,
  volume,
  showGrowthIcon = false,
  subSectionForAmplitudeTracking,
  isApiError = false,
}) => {
  const pagesConfig = usePageConfig();
  const { pageName, pageType } = pagesConfig;
  //Start Page Element View Amplitude Tracking
  const { track } = useAnalytics();
  const mainFiltersStateRO = useSelector((state: IRootState) => state.mainFiltersRO);
  const mainFilterStateMarketPremium = useSelector((state: IRootState) => state.mainFiltersMarketPremium);
  const { totalAvailablePgs, totalSubscriptionPgs } = useSelector((state: IRootState) => state.availabilityPg);
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, pageName);

  const createParam: () => PageElementViewTrackingParam = () => {
    return {
      pageName: pageName,
      pageType: pageType,
      eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
      retailerClientState: retailerClientState,
      track: track,
      sideFilters: sideFiltersState.appliedFilters,
      componentName: PERFORMANCE_CARD_TITLE,
      elementType: ElementType.TILES,
      elementKpi: KpiTextForTracking(volume)[performanceType as PerformanceType],
      mainFilterState:
        pagesConfig.pageType === PageType.RETAILER_OVERVIEW ? mainFiltersStateRO : mainFilterStateMarketPremium,
      hasData: !!revenuePerformance && !!volumePerformance,
      initialLocationId: getInitialAppliedCountries(pagesConfig, pageType, effectiveProductType),
      access: pagesConfig.access,
      subSection: subSectionForAmplitudeTracking,
    };
  };
  const { ref: MarketPerformanceComponent } = useInViewTracking(createParam);
  //End Page Element View Amplitude Tracking

  //Add element view tracking upon first load
  useEffect(() => {
    const params = createParam();
    pageElementViewTrackingFunction(params);
  }, []);

  const appliedFiltersInfo = useMemo(() => {
    if (pageName === PageName.RETAILER_OVERVIEW) {
      return;
    }
    const appliedFilters = pagesConfig.appliedFilters[pageType]?.selectedFilters;

    return getAppliedPgAndCountriesInfo(appliedFilters, totalAvailablePgs, totalSubscriptionPgs, pageName);
  }, [pagesConfig, totalAvailablePgs, totalSubscriptionPgs]);

  const tileConfig = getPosFactTileConfig(pageName, performanceType);

  const performanceTypeMapping = {
    [PerformanceType.Value]: revenuePerformance,
    [PerformanceType.Volume]: volumePerformance,
  };

  return renderVisualization({
    isLoading,
    showLoader: false,
    fetchedApiData: volumePerformance ?? revenuePerformance,
    defaultData: new SalesPerformance(),
    noResultPredicate: () => !volumePerformance || !revenuePerformance,
    jsxToRender: (
      <div
        ref={MarketPerformanceComponent}
        data-test-id="market-performance-tiles"
        className="retail-market-performance-tiles"
      >
        {appliedFiltersInfo && (
          <div className="text-lg mb-5" data-test-id="tiles-filter-summary">
            {appliedFiltersInfo}
          </div>
        )}
        <WithTiles>
          {tileConfig?.map(
            ({
              salesPerformanceType,
              kpiType,
              tileType,
              tileTitle,
              marketShareType,
              ...restParam
            }: PosFactTileConfig) => {
              const updatedTitle =
                salesPerformanceType === PerformanceType.Volume
                  ? tileTitle(volume)
                  : tileTitle(constructRevenueTitle(factRevenue));
              return (
                <WithTile testId={`${kebabCase(updatedTitle)}-tile`} key={updatedTitle} isLoading={isLoading}>
                  <PosFactTile
                    selectedGrowthType={selectedGrowthType}
                    salesPerformance={performanceTypeMapping[salesPerformanceType]!}
                    factRevenue={factRevenue}
                    kpiType={kpiType}
                    tileType={tileType}
                    tileTitle={updatedTitle}
                    marketShareType={marketShareType}
                    showGrowthIcon={showGrowthIcon}
                    {...restParam}
                  />
                </WithTile>
              );
            },
          )}
        </WithTiles>
      </div>
    ),
    noResultJsx: <div ref={MarketPerformanceComponent}>{NoResult()}</div>,
    errorMessageJsx: <div ref={MarketPerformanceComponent}>{ErrorResultMessage()}</div>,
    isError: isApiError,
  });
};

export default MarketPerformancePosFactsTiles;
